<template>
  <b-modal
    v-model="modalActive"
    :width="'auto'"
    scroll="keep"
    class="mobile-filter-modal"
    has-modal-card
    trap-focus
  >
    <div class="modal-card mobile-filter-card" style="max-height: 80vh;">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Filters
          <span v-if="modifiedFiltersCount > 0" class="filter-count-badge">
            {{ modifiedFiltersCount }}
          </span>
        </p>
        <button class="delete is-large" type="button" @click="closeModal" />
      </header>
      <section class="modal-card-body">
        <!-- Mobile filter form -->
        <form class="mobile-filters">
          <!-- Price Range -->
          <div class="filter-section">
            <h3
              class="filter-title"
              :class="{ 'has-text-grey': !resultsLoaded }"
            >
              Price
            </h3>

            <b-slider
              v-if="maxPrice > 0"
              v-model="priceRangeLocal"
              :min="minPrice"
              :max="maxPrice"
              :step="1"
              class="price-slider"
              tooltip-always
              lazy
            />

            <div class="price-inputs" v-if="maxPrice > 0">
              <div class="field has-addons">
                <p class="control">
                  <a class="button is-static narrow">$</a>
                </p>
                <p class="control">
                  <input
                    v-model.lazy="priceRangeMin"
                    class="input"
                    type="number"
                    placeholder="Min"
                    :disabled="!resultsLoaded"
                  />
                </p>
              </div>

              <div class="field has-addons">
                <p class="control">
                  <a class="button is-static narrow">$</a>
                </p>
                <p class="control">
                  <input
                    v-model.lazy="priceRangeMax"
                    class="input"
                    type="number"
                    placeholder="Max"
                    :disabled="!resultsLoaded"
                  />
                </p>
              </div>
            </div>

            <div v-else class="price-inputs">
              <div class="field has-addons" style="width:49%;opacity: 0.3;">
                <b-skeleton
                  width="100%"
                  height="40px"
                  :animated="false"
                ></b-skeleton>
              </div>

              <div class="field has-addons" style="width:49%;opacity: 0.3;">
                <b-skeleton
                  width="100%"
                  height="40px"
                  :animated="false"
                ></b-skeleton>
              </div>
            </div>
          </div>

          <!-- Radio Filters -->
          <div
            v-for="section in radioFilters"
            :key="section.id"
            class="filter-section"
          >
            <div
              class="trigger-button is-flex"
              :class="{ 'has-text-grey': !resultsLoaded }"
            >
              <h3 class="filter-title is-5">{{ section.name }}</h3>
            </div>

            <div class="filter-options has-max-height-scroll">
              <b-field
                v-for="option in section.options"
                :key="option.value"
                class="mb-1"
              >
                <b-radio
                  v-model="section.selected"
                  :native-value="option.value"
                  :disabled="!resultsLoaded"
                >
                  <div class="option-label">
                    <b-icon
                      pack="fas"
                      v-if="option.icon"
                      :icon="option.icon"
                      size="is-small"
                      class="mr-2"
                    />
                    {{ option.label }}
                  </div>
                </b-radio>
              </b-field>
            </div>
          </div>

          <!-- Checkbox Filters -->
          <div
            v-for="section in checkboxFilters"
            :key="section.id"
            class="filter-section"
          >
            <div
              class="trigger-button is-flex"
              :class="{ 'has-text-grey': !resultsLoaded }"
            >
              <h3 class="filter-title is-5">{{ section.name }}</h3>
            </div>

            <div class="filter-options has-max-height-scroll">
              <template v-if="section.highlightedOptions">
                <b-field
                  v-for="option in section.highlightedOptions"
                  :key="option.value"
                  class="mb-1 border-b-2 border-grey-200"
                >
                  <b-checkbox
                    v-model="section.selected"
                    :native-value="option.value"
                    :disabled="!resultsLoaded"
                  >
                    <div class="option-label">
                      <b-icon
                        pack="fas"
                        v-if="option.icon"
                        :icon="option.icon"
                        size="is-small"
                        class="mr-2"
                      />
                      {{ option.label }}

                      <i
                        class="fa fa-solid fa-star ml-1 has-text-success fa-sm"
                      />
                    </div>
                  </b-checkbox>
                </b-field>
              </template>

              <b-field
                v-for="option in section.options"
                :key="option.value"
                class="mb-1"
              >
                <b-checkbox
                  v-model="section.selected"
                  :native-value="option.value"
                  :disabled="!resultsLoaded"
                >
                  <div class="option-label">
                    <b-icon
                      pack="fas"
                      v-if="option.icon"
                      :icon="option.icon"
                      size="is-small"
                      class="mr-2"
                    />
                    {{ option.label }}
                  </div>
                </b-checkbox>
              </b-field>
            </div>
          </div>

          <!-- Company Preferred Hotels Filter -->
          <div v-if="hasPreferredHotels" class="filter-section">
            <div class="is-flex" :class="{ 'has-text-grey': !resultsLoaded }">
              <h3 class="filter-title is-5">Company Preferred Hotels</h3>
            </div>

            <div class="filter-options has-max-height-scroll">
              <b-field
                v-for="option in filters.companyPreferred.options"
                :key="option.value"
                class="mb-1"
              >
                <b-radio
                  v-model="filters.companyPreferred.selected"
                  :native-value="option.value"
                  :disabled="!resultsLoaded"
                >
                  {{ option.label }}
                </b-radio>
              </b-field>
            </div>
          </div>
        </form>
      </section>

      <footer class="modal-card-foot mobile-filter-footer is-flex is-justify-content-end">
        <div class="filter-actions ">
          <button
            @click="resetFilters"
            type="button"
            class="button clear-button is-default"
            :disabled="modifiedFiltersCount === 0"
          >
            <span>Clear filters</span>
          </button>
          <button
            @click="closeModal"
            type="button"
            class="button apply-button is-primary"
          >
            <span>Done</span>
          </button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapGetters } from "vuex"
//import HotelMapModal from '@/components/GMap/HotelMapModal.vue';

/**
 * FilterModal - A self-contained modal filter component
 *
 * This component provides a mobile-optimized filter interface that directly
 * accesses the hotelSearch Vuex store state, eliminating the need for prop drilling.
 *
 * Features:
 * - Price range slider and inputs with direct store mutations
 * - Company preferred hotels filter
 * - Radio button filters (policy, etc.)
 * - Checkbox filters (amenities, chains, brands)
 * - Hotel map modal integration
 * - Automatic computation of filter states from store
 *
 * @emits update:isActive - When modal visibility changes
 * @emits close - When modal is closed
 */
export default {
  name: "FilterModal",

  components: {
    //HotelMapModal: () => import("@/components/GMap/HotelMapModal.vue"),
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    showMapLauncher: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState({
      minPrice: (state) => state.hotelSearch.minPrice,
      maxPrice: (state) => state.hotelSearch.maxPrice,
      filters: (state) => state.hotelSearch.filters,
      resultsLoaded: (state) => state.hotelSearch.searchResults.length > 0,
      searchResults: (state) => state.hotelSearch.searchResults,
      searchMeta: (state) => state.hotelSearch.searchMeta,
      visibleHotels: (state) =>
        state.hotelSearch.searchResults.filter((hotel) => hotel.isVisible),
    }),
    ...mapGetters({
      filteredResults: "hotelSearch/filteredResults",
      modifiedFiltersCount: "hotelSearch/modifiedFiltersCount",
    }),

    modalActive: {
      get() {
        return this.isActive
      },
      set(value) {
        this.$emit("update:isActive", value)
      },
    },

    hasPreferredHotels() {
      return this.searchResults?.some((hotel) => hotel.isPreferred)
    },

    checkboxFilters() {
      return [this.filters.brands, this.filters.chains, this.filters.amenities]
    },

    radioFilters() {
      return [this.filters.policy]
    },

    priceRangeLocal: {
      get() {
        return [this.filters.price.range[0], this.filters.price.range[1]]
      },
      set(value) {
        this.$store.commit("hotelSearch/SET_PRICE_FILTER_RANGE", value)
      },
    },

    priceRangeMin: {
      get() {
        return this.filters.price.range[0]
      },
      set(value) {
        const newRange = [parseInt(value) || 0, this.filters.price.range[1]]
        this.$store.commit("hotelSearch/SET_PRICE_FILTER_RANGE", newRange)
      },
    },

    priceRangeMax: {
      get() {
        return this.filters.price.range[1]
      },
      set(value) {
        const newRange = [this.filters.price.range[0], parseInt(value) || 0]
        this.$store.commit("hotelSearch/SET_PRICE_FILTER_RANGE", newRange)
      },
    },
  },

  methods: {
    closeModal() {
      this.$emit("update:isActive", false)
      this.$emit("close")
    },
    resetFilters() {
      const maxPrice = this.searchMeta.priceRange.max
      const minPrice = this.searchMeta.priceRange.min

      //To Do: Add Toast Message
      this.$store.commit("hotelSearch/RESET_FILTERS")

      this.$store.commit("hotelSearch/SET_PRICE_RANGE", [minPrice, maxPrice])
    },
  },
}
</script>

<style lang="scss" scoped>
/* Modal container improvements */
:deep(.mobile-filter-modal) {
  .modal-card {
    margin: 0.5rem;
    max-height: calc(100vh - 1rem);
    width: calc(100vw - 1rem);
    max-width: 600px;
    display: flex;
    flex-direction: column;
  }

  @media screen and (max-width: 768px) {
    .modal-card {
      margin: 0;
      max-height: 100vh;
      width: 100vw;
      border-radius: 0;
    }
  }
}

.mobile-filter-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.modal-card-head {
  flex-shrink: 0;
  padding: 1.25rem;
  border-bottom: 1px solid #dbdbdb;

  .modal-card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #555555;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.filter-count-badge {
  background-color: #3273dc;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 1.5rem;
  text-align: center;
  line-height: 1;
}

.modal-card-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1rem 1.25rem;

  /* Ensure smooth scrolling on mobile */
  scroll-behavior: smooth;

  /* Filter sections */
  .filter-section {
    padding: 0rem 0rem 0.75rem 0rem;
    border-bottom: 1px solid #f0f0f0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 1rem;
    color: #363636;
  }

  /* Collapsible sections */
  /*
  .trigger-button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 0.75rem 0;
    min-height: 48px; 
          background-color: rgb(147, 23, 23);
      color: white;
      border-radius: 4px;
      margin: 0 -0.5rem;
      padding: 0.75rem 0.5rem;

    .title {
        color: white;
        margin-bottom: 0;
        font-size: 1.125rem;
        font-weight: 600;
    }

    &:hover {
      background-color: rgb(147, 23, 23);
      color: white;

      .title {
        color: white;

      }
    }
  }

  */
}

.mobile-filter-footer {
  flex-shrink: 0;
  padding: 1rem 1.25rem;
  border-top: 1px solid #dbdbdb;
  background-color: #fafafa;
}

/* Scrollable filter options */
.has-max-height-scroll {
  max-height: 20rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 0.25rem; /* Space for scrollbar */

  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
}

/* Price section improvements */
.price-slider {
  margin: 1.5rem 0;
  padding: 0 0.5rem;
}

.price-inputs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.field.has-addons {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
  flex: 1;
}

/* Form elements */
.option-label {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  line-height: 1.4;
  min-height: 1.5rem;
}

/* Button improvements */
.filter-actions {
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  justify-content: space-between;
  justify-items: space-between;

  .button {
    flex: 1;
    min-height: 48px; /* Adequate touch target */
    font-weight: 500;
  }
}

.button.narrow {
  padding-left: 0.5em;
  padding-right: 0.5em;
}

/* Enhanced touch targets for mobile */
:deep(.b-radio),
:deep(.b-checkbox) {
  .control-label {
    min-height: 44px;
    display: flex;
    align-items: center;
    padding: 0.5rem 0;
  }
}

/* Improved spacing for form fields */
.b-field {
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
}

/* Loading state improvements */
.b-skeleton {
  border-radius: 4px;
}
</style>
